/**
 * @file このステップで用意するもの (道具・環境) のチェックリスト。
 *
 * @what: カタログ側 (attainment_v1.yaml の preparations) を 教材 / 道具 / 環境 に分けて
 *   並べ、お子さんを選んでいるときは「そろった」をチェックできる。残り数を見出しに出す。
 *   そろったものは「購入済み」にたたみ、まだのものが先に目に入るようにする (代表 2026-08-24)。
 * @why: 「親として何を用意すべきか」への直接の答え (代表 2026-08-20)。実物の教材は
 *   教材の面で登録する。
 * @failure: 描画とチェックのみ。チェック状態は draft (prepStates) が持ち、保存バーで PUT される。
 */

import type { Preparation, PreparationKind } from "../model";
import { PREPARATION_KIND_ICONS } from "./studyIcons";
import "./study.css";

const KIND_LABELS: Record<PreparationKind, string> = {
  material: "教材",
  tool: "道具",
  environment: "環境",
};

const KIND_ORDER: readonly PreparationKind[] = ["material", "tool", "environment"];

export function PreparationsCard({
  preparations,
  prepStates,
  onToggle,
}: {
  preparations: Preparation[];
  /** undefined = お子さん未選択 (チェック無しの一覧表示のみ)。 */
  prepStates?: Record<string, boolean>;
  onToggle?: (prepId: string) => void;
}) {
  if (preparations.length === 0) return null;
  const editable = prepStates !== undefined && onToggle !== undefined;
  const isDone = (p: Preparation) => editable && prepStates[p.id] === true;
  const todo = preparations.filter((p) => !isDone(p));
  const done = preparations.filter(isDone);

  const renderItem = (p: Preparation) => (
    <li key={p.id} className={isDone(p) ? "study-prep-item study-prep-item--done" : "study-prep-item"}>
      {editable ? (
        <label className="study-prep-label">
          <input type="checkbox" checked={isDone(p)} onChange={() => onToggle(p.id)} />
          <span aria-hidden="true">{PREPARATION_KIND_ICONS[p.kind]}</span>
          <span>{p.label}</span>
        </label>
      ) : (
        <span className="study-prep-label">
          <span aria-hidden="true">{PREPARATION_KIND_ICONS[p.kind]}</span>
          <span>{p.label}</span>
        </span>
      )}
      {p.note ? <div className="study-prep-note">{p.note}</div> : null}
    </li>
  );

  return (
    <section className="study-prep-block" aria-label="このステップで用意するもの">
      <h3 className="study-subsection-title">
        <span aria-hidden="true">🎒</span> このステップで用意するもの
        {editable ? (
          <span className="study-card-title-sub">
            {todo.length === 0 ? "すべてそろいました" : `あと ${todo.length} 件`}
          </span>
        ) : null}
      </h3>
      {todo.length > 0 ? (
        <div className="study-prep-groups">
          {KIND_ORDER.map((kind) => {
            const items = todo.filter((p) => p.kind === kind);
            if (items.length === 0) return null;
            return (
              <div key={kind} className="study-prep-group">
                <div className="study-prep-group-title">
                  <span aria-hidden="true">{PREPARATION_KIND_ICONS[kind]}</span> {KIND_LABELS[kind]}
                </div>
                <ul className="study-prep-list">{items.map(renderItem)}</ul>
              </div>
            );
          })}
        </div>
      ) : null}
      {done.length > 0 ? (
        <details className="study-prep-done">
          <summary>購入済み {done.length} 件</summary>
          <ul className="study-prep-list">{done.map(renderItem)}</ul>
        </details>
      ) : null}
    </section>
  );
}
